import { useEffect, useState } from "react";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { showError, showSuccess } from "@/utils/toast";

export type AuthUser = {
  id: string;
  email?: string;
};

export const useAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [ready, setReady] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setReady(true);
      return;
    }

    // Carrega sessão inicial
    supabase.auth.getSession().then(({ data }) => {
      updateLocalState(data?.session?.user ?? null);
      setReady(true);
    });

    // Escuta mudanças de auth
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      updateLocalState(session?.user ?? null);
    });

    return () => {
      sub?.subscription.unsubscribe();
    };
  }, []);

  const updateLocalState = (u: any | null) => {
    setUser(u ? { id: u.id, email: u.email } : null);
    if (u?.id) {
      supabase
        .from("profiles")
        .select("role, status")
        .eq("id", u.id)
        .maybeSingle()
        .then(({ data: prof }) => {
          setRole((prof as any)?.role ?? "user");
          setIsDisabled(((prof as any)?.status ?? "active") !== "active");
        });
    } else {
      setRole(null);
      setIsDisabled(false);
    }
  };

  // Heartbeat de presença
  useEffect(() => {
    if (!user?.id || !isSupabaseConfigured) return;
    const updatePresence = async () => {
      await supabase
        .from("profiles")
        .update({ is_online: true, last_seen: new Date().toISOString() })
        .eq("id", user.id);
    };
    updatePresence();
    const intervalId = window.setInterval(updatePresence, 30000);
    return () => window.clearInterval(intervalId);
  }, [user?.id]);

  const login = async (email: string, password: string) => {
    if (!isSupabaseConfigured) return showError("Supabase não configurado.");
    setLoading(true);
    try {
      // Usa a função invoke, que injeta automaticamente a URL e Anon Key corretas
      const { data, error } = await supabase.functions.invoke("auth-login", {
        body: { email, password },
      });

      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error || "Erro ao fazer login");

      const { session } = data;
      const { error: setErr } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      });
      if (setErr) throw setErr;

      showSuccess("Login realizado com sucesso!");
    } catch (error: any) {
      showError(error.message || "Erro ao fazer login.");
    } finally {
      setLoading(false);
    }
  };

  const signup = async (fullName: string, email: string, password: string) => {
    if (!isSupabaseConfigured) return showError("Supabase não configurado.");
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("auth-signup", {
        body: { email, password, fullName },
      });

      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error || "Erro ao criar conta");

      showSuccess(data.message || "Conta criada com sucesso!");
    } catch (error: any) {
      showError(error.message || "Erro ao criar conta.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    if (!isSupabaseConfigured) return;
    if (user?.id) {
      await supabase
        .from("profiles")
        .update({ is_online: false, last_seen: new Date().toISOString() })
        .eq("id", user.id);
    }
    await supabase.auth.signOut();
    setUser(null);
    setRole(null);
    showSuccess("Você saiu da conta.");
  };

  return { user, role, loading, ready, login, signup, logout, isSupabaseConfigured, isDisabled };
};